/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import Async from 'react-select';
import 'react-select/dist/react-select.css';

type ExtraSkillOption = {
  label: string;
  icon_name: string;
};

type ExtraSkillsData = {
  [key: string]: {
    label: string;
    icon_name?: string;
    true?: ExtraSkillOption;
    false?: ExtraSkillOption;
  };
}

type TSelectedOption = {
    value: string;
    label: string;
    iconName: string
}

type TSelectedRole = {
    name: string;
    value: string;
    iconName: string
}

type TSelectRoleComponent = {
    serviceKey: string;
    roleData: any;
    serviceStructure: any;
    selectedRole: TSelectedRole;
    update: (selectedRole: TSelectedRole) => void;
    disabled: boolean;
};

export default function SelectExtraSkillsComponent({
    serviceKey,
    roleData,
    serviceStructure,
    selectedRole,
    update,
    disabled,
}: TSelectRoleComponent) {

    const [selectedSkills, setSelectedSkills] = useState([]);
    const [extraSkills, setExtraSkills] = useState([]);

    const setOptionsAndDefaultValues = (structure: any) => {
        if (structure) {
            const serviceKeyData = serviceStructure[serviceKey];
            const roleFilterName = serviceKeyData?.role_filter_name;
            const extraSkillsData: ExtraSkillsData = serviceKeyData?.[roleFilterName][selectedRole.value] || {};

            if (extraSkillsData) {
                const options = Object.entries(extraSkillsData)
                    .filter(([key]) => !['label', 'icon_name'].includes(key))
                    .flatMap(([key, value]) => {
                        if (value.true) {
                            return [{
                                label: value.true.label,
                                value: key,
                                iconName: value.true.icon_name,
                            }];
                        }
                        return [];
                    });
                setExtraSkills(options);

                const skillsData = roleData[serviceKey];

                const defaultSelectedSkillsLabels = Object.entries(skillsData)
                    .filter(([, value]) => +value)
                    .map(([key]) => key);
                const defaultSelectedSkills = options.filter(skill =>
                    defaultSelectedSkillsLabels.includes(skill.value)
                );
                setSelectedSkills(defaultSelectedSkills);
                setExtraSkills(options.filter(skill =>
                    !defaultSelectedSkillsLabels.includes(skill.value)
                ));

            }
        }
    };

    useEffect(() => {
        setOptionsAndDefaultValues(serviceStructure);
    }, [selectedRole]);

    const handleSelectChange = (selectedSkill: any) => {
        update(selectedSkill);
    };

    const handleSelectWrapper = (selectedOptions: any) => {
        const lastSelectedOption = selectedOptions.find((option: { value: any; }) =>
            !selectedSkills.some(selectedOption => selectedOption.value === option.value)
        );
        if (lastSelectedOption) {
            const event = {
                currentTarget: {
                    name: lastSelectedOption.value,
                    value: '1',
                }
            };
            handleSelectChange(event);
            setSelectedSkills(selectedOptions || []);
            setExtraSkills(prevExtraSkills =>
                prevExtraSkills.filter(skill => skill.value !== lastSelectedOption.value)
            );
        }
    };

    const removeSkill = (valueToRemove: string) => {
        const selectedOption = selectedSkills.find(skill => skill.value === valueToRemove);
        const event = {
            currentTarget: {
                name: valueToRemove,
                value: '0',
            }
        };
        handleSelectChange(event);
        setSelectedSkills(selectedSkills.filter(option => option.value !== valueToRemove));
        setExtraSkills(prevSelectedSkills => [...prevSelectedSkills, selectedOption]);
    };

    const getDefaultIconName = (extraSkillIconName: string) => {
        const serviceKeyData = serviceStructure[serviceKey];
        const roleFilterName = serviceKeyData?.role_filter_name;
        const defaultIconName: string = serviceKeyData?.[roleFilterName][selectedRole.value]?.icon_name || '';

        return extraSkillIconName || defaultIconName;
    };

    const renderOption = (option: TSelectedOption) => (
        <div className="role-list-container">
            <i className={`icon ${getDefaultIconName(option.iconName)} normal role-list-logo`}/>
            <div className="role-description-container">
                <div>{option.label} </div>
            </div>
        </div>
    );

    const arrowRender = () => (
        <div className='arrow collapsed'>
            <span className="chevron"/>
        </div>
    );

    return (
        <div className='select-extra-skills-container'>
            <form>
                <Async
                    multi={true}
                    className="swiper-no-swiping"
                    disabled={disabled}
                    onChange={handleSelectWrapper}
                    options={extraSkills}
                    placeholder="Add..."
                    simpleValue={false}
                    value={selectedSkills}
                    optionRenderer={renderOption}
                    arrowRenderer={arrowRender}
                />
                <br/>
                <div className='extra-skills-list-container'>
                    {selectedSkills.map((option, index) => (
                        <div key={`selected_skill_${option.value}`} className="extra-skills-icon-area">
                            <div
                                /* eslint-disable-next-line react/no-array-index-key */
                                key={`selected_skill_${option.value}_${index}`}
                                className='role-item active'
                            >
                                <div className="item-circle-wrapper" onClick={() => removeSkill(option.value)}>
                                    <i
                                        className={`icon ${getDefaultIconName(option.iconName)} active icon-remove-circle-1`}
                                        aria-label={`Remove skill ${option.label}`}
                                    />
                                    <i
                                        className="icon icon-remove-circle-11 red"
                                        aria-label={`Remove skill ${option.label}`}
                                    />
                                </div>
                                <p className="role-item-name">{option.label}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </form>
        </div>
    );
}
